import React, { useEffect, useState, useCallback } from 'react';
import { Input, Space, Table, Button, message, Alert } from 'antd';
import { SearchOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import StandardDrawer from '../../components/common/StandardDrawer';
import * as personnelService from '../../services/personnel/personnelService';
import EllipsisCell from '@/components/common/EllipsisCell';

/**
 * BindPersonnelDrawer - Bind/unbind personnel to a level using transfer-like pattern
 */
const BindPersonnelDrawer = ({ visible, level, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Left panel: available personnel
  const [availableData, setAvailableData] = useState([]);
  const [availableTotal, setAvailableTotal] = useState(0);
  const [availablePage, setAvailablePage] = useState(1);
  const [selectedLeftKeys, setSelectedLeftKeys] = useState([]);

  // Right panel: chosen personnel
  const [chosenList, setChosenList] = useState([]);
  const [selectedRightKeys, setSelectedRightKeys] = useState([]);

  // Search & filter
  const [keyword, setKeyword] = useState('');

  const PAGE_SIZE = 50;

  const fetchAvailable = useCallback(async (page = 1, searchKeyword) => {
    if (!level) return;
    setLoading(true);
    try {
      const params = {
        nameLike: searchKeyword ?? keyword,
        loginIdLike: searchKeyword ?? keyword,
        currentPage: page,
        pageSize: PAGE_SIZE,
      };
      const response = await personnelService.queryPersonnel(params);
      if (response.success) {
        setAvailableData(response.data?.tableData || []);
        setAvailableTotal(response.data?.paging?.totalCount || 0);
        setAvailablePage(page);
      }
    } catch (error) {
      console.error('Fetch personnel failed:', error);
    } finally {
      setLoading(false);
    }
  }, [level, keyword]);

  // Load bound personnel when drawer opens
  useEffect(() => {
    if (visible && level) {
      setChosenList([]);
      setSelectedLeftKeys([]);
      setSelectedRightKeys([]);
      setKeyword('');

      // Fetch bound personnel and pre-populate right panel
      personnelService.getBoundPersonnel(level.id).then((res) => {
        if (res.success && res.data) {
          setChosenList(res.data);
        }
      });

      fetchAvailable(1, '');
    }
  }, [visible, level]);

  const handleSearch = () => {
    setSelectedLeftKeys([]);
    fetchAvailable(1, keyword);
  };

  const handleLevelFilterChange = (value) => {
    setSelectedLeftKeys([]);
    fetchAvailable(1, keyword);
  };

  const handleMoveRight = () => {
    const toAdd = availableData.filter(
      (p) => selectedLeftKeys.includes(p.loginId) && !chosenList.some((c) => c.loginId === p.loginId)
    );
    if (toAdd.length > 0) {
      setChosenList((prev) => [...prev, ...toAdd]);
    }
    setSelectedLeftKeys([]);
  };

  const handleMoveLeft = () => {
    setChosenList((prev) => prev.filter((p) => !selectedRightKeys.includes(p.loginId)));
    setSelectedRightKeys([]);
  };

  const handleRemoveFromRight = (loginId) => {
    setChosenList((prev) => prev.filter((p) => p.loginId !== loginId));
  };

  const handleSubmit = async () => {
    if (!level) return;
    setSubmitLoading(true);
    try {
      const response = await personnelService.bindPersonnel({
        levelId: level.id,
        loginIds: chosenList.map((p) => p.loginId),
      });
      if (response.success) {
        message.success(t('personnel.bindSuccess'));
        onSuccess();
      } else {
        message.error(response.errorMessage || t('personnel.bindFailed'));
      }
    } catch (error) {
      console.error('Bind failed:', error);
      message.error(t('personnel.bindFailed'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const leftColumns = [
    {
      title: t('personnel.personnelName'),
      dataIndex: 'name',
      key: 'name',
      width: 100,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('personnel.employeeCode'),
      dataIndex: 'loginId',
      key: 'loginId',
      width: 80,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('personnel.currentLevel'),
      key: 'proficiency',
      width: 90,
      render: (_, record) => <EllipsisCell>{record.extendProps?.proficiency || record.proficiency || '-'}</EllipsisCell>,
    },
  ];

  const rightColumns = [
    {
      title: t('personnel.personnelName'),
      dataIndex: 'name',
      key: 'name',
      width: 100,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('personnel.employeeCode'),
      dataIndex: 'loginId',
      key: 'loginId',
      width: 80,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('personnel.currentLevel'),
      key: 'proficiency',
      width: 90,
      render: (_, record) => <EllipsisCell>{record.extendProps?.proficiency || record.proficiency || '-'}</EllipsisCell>,
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Button
          type="link"
          danger
          size="small"
          onClick={() => handleRemoveFromRight(record.loginId)}
        >
          x
        </Button>
      ),
    },
  ];

  return (
    <StandardDrawer
      visible={visible}
      onClose={onClose}
      onOk={handleSubmit}
      title={`${t('personnel.bindPersonnelTitle')} - ${level?.levelName || ''}`}
      confirmLoading={submitLoading}
      okText={t('personnel.confirmBind')}
      width={960}
      maskClosable={false}
      destroyOnClose={true}
    >
      <div style={{ padding: '16px 0' }}>
        {/* Search */}
        <Space wrap style={{ marginBottom: 16, width: '100%' }}>
          <Input
            placeholder={t('personnel.searchPlaceholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 320 }}
            prefix={<SearchOutlined />}
          />
          <Button type="primary" onClick={handleSearch}>
            {t('common.search')}
          </Button>
        </Space>

        <Alert
          type="info"
          showIcon
          message={t('personnel.bindTip')}
          style={{ marginBottom: 16 }}
        />

        {/* Transfer-like layout */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* Left: Available pool */}
          <div style={{ flex: 1, border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', background: '#fafafa', borderBottom: '1px solid #d9d9d9', fontWeight: 'bold' }}>
              {t('personnel.availablePool')} ({availableTotal})
            </div>
            <Table
              columns={leftColumns}
              dataSource={availableData.filter((p) => !chosenList.some((c) => c.loginId === p.loginId))}
              loading={loading}
              rowKey="loginId"
              size="small"
              pagination={{
                current: availablePage,
                pageSize: PAGE_SIZE,
                total: availableTotal,
                showSizeChanger: false,
                size: 'small',
                onChange: (page) => fetchAvailable(page),
              }}
              rowSelection={{
                selectedRowKeys: selectedLeftKeys,
                onChange: (keys) => setSelectedLeftKeys(keys),
              }}
              scroll={{ y: 400 }}
            />
          </div>

          {/* Middle: Transfer buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 180 }}>
            <Button
              icon={<RightOutlined />}
              onClick={handleMoveRight}
              disabled={selectedLeftKeys.length === 0}
              type="primary"
              size="small"
            />
            <Button
              icon={<LeftOutlined />}
              onClick={handleMoveLeft}
              disabled={selectedRightKeys.length === 0}
              size="small"
            />
          </div>

          {/* Right: Chosen list */}
          <div style={{ flex: 1, border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', background: '#fafafa', borderBottom: '1px solid #d9d9d9', fontWeight: 'bold' }}>
              {t('personnel.chosenList')} ({chosenList.length})
            </div>
            <Table
              columns={rightColumns}
              dataSource={chosenList}
              rowKey="loginId"
              size="small"
              pagination={false}
              rowSelection={{
                selectedRowKeys: selectedRightKeys,
                onChange: (keys) => setSelectedRightKeys(keys),
              }}
              scroll={{ y: 400 }}
            />
          </div>
        </div>
      </div>
    </StandardDrawer>
  );
};

export default BindPersonnelDrawer;
